// @flow
import React, { Component } from 'react';
import { History } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
// redux
import { connect } from 'react-redux';
import { fetchAllResourcesExclParams } from '../../actions/resources';
import userActions from '../../actions/user';
// models
import type { ResourceType } from '../../types/resource';
import type { UserIncludeOnboarding } from '../../types/user';
// shared components
import AppView from '../layouts/AppView';
import DashboardCards from '../dashboard-cards';
import Dropdown from '../shared/DropdownCustom';
import Text from '../shared/Typography';
import Button from '../shared/Button';
import FilterList, { FilterButton } from '../shared/FilterList';
import Message from '../shared/Message';
import OnboardingStateHeader from '../onboarding-state-header';
import Section from '../shared/PageSection';
import Loader from '../shared/Loader';
// utils
import sortingData from '../../data/sorting';
import { alpha } from '../../utils/utils';
import { getRouteTFunction } from '../../i18n';

type Props = {
  user: {
    data: ?UserIncludeOnboarding,
    loading: boolean
  },
  requests: Object,
  resources: {
    data: ResourceType[],
    loading: boolean
  },
  fetchResourcesExcluding: () => void,
  fetchUser: () => void,
  history: History,
  location: {
    state?: {
      user: UserIncludeOnboarding
    },
    messageText: string,
    messageType: string
  },
  recordTypes: Object[],
  patientsInRecords: Set,
  loading?: boolean
} & WithTranslation

type State = {
  loading: boolean,
  activeTypes: ResourceType[],
  activeYears: string[],
  activeProviders: string[],
  sortingType: string,
  selectedTypes: ResourceType[],
  selectedPatients: string[],
  shareSelectOn: boolean,
  selectedCards: any[],
  selectAll: boolean
}
class Dashboard extends Component<Props, State> {
  state: State = {
    loading: false,
    activeTypes: [],
    activeYears: [],
    activeProviders: [],
    sortingType: 'dateDesc',
    selectedTypes: [],
    selectedPatients: [],
    shareSelectOn: false,
    selectedCards: [],
    selectAll: false
  }

  componentDidMount() {
    const resources = this.props.resources;
    if (!resources || resources.length === 0) {
      // to prevent making a call each time users go back to dash, for speed
      this.props.fetchResourcesExcluding('nested,symptoms');
    }
    this.props.fetchUser(['onboarding_state']);
  }

  getFilteredResources = (resources) => {
    const { activeTypes, activeYears, activeProviders } = this.state;
    // const query = this.state.searchQuery.toLowerCase();
    let filteredResources = resources;

    if (activeTypes.length) {
      filteredResources = filteredResources
        .filter((resource) => activeTypes.includes(resource.resource_type));
    }

    if (activeYears.length) {
      filteredResources = filteredResources
        .filter((resource) => activeYears
          .includes(resource.sortByDate.format('YYYY')));
    }

    if (activeProviders.length) {
      filteredResources = filteredResources
        .filter((resource) =>
          activeProviders.includes(resource.institution_name) ||
          (activeProviders.includes('my_upload') && resource.source === 'patient')
        );
    }

    // if (query && query.length) {
    //   filteredResources = filteredResources.filter((res) => (
    //     this.containsQuery(res.searchableText)
    //   ));
    // }

    return filteredResources;
  }


  sortResources = (a, b, sortingAttribute, ascending = true) => {
    if (!ascending) {
      if (a[sortingAttribute] < b[sortingAttribute]) return 1;
      if (b[sortingAttribute] < a[sortingAttribute]) return -1;
    } else if (ascending) {
      if (a[sortingAttribute] < b[sortingAttribute]) return -1;
      if (b[sortingAttribute] > a[sortingAttribute]) return 1;
    }
    return null;
  }

  updateType = (filter) => {
    let newActiveTypes = [];
    const existsInFilterList = this.state.activeTypes.includes(filter);
    if (this.state.activeTypes.length > 0) newActiveTypes = this.state.activeTypes;
    if (existsInFilterList) newActiveTypes = newActiveTypes.filter((item) => item !== filter);
    else if (!existsInFilterList) newActiveTypes.push(filter);
    this.setState({ activeTypes: newActiveTypes });
  }

  updateYear = (year) => {
    let newActiveYears = [];
    const existsInFilterList = this.state.activeYears.includes(year);
    if (this.state.activeYears.length > 0) newActiveYears = this.state.activeYears;
    if (existsInFilterList) newActiveYears = newActiveYears.filter((item) => item !== year);
    else if (!existsInFilterList) newActiveYears.push(year);
    this.setState({ activeYears: newActiveYears });
  }

  updateProvider = (provider) => {
    let newActiveProviders = [];
    const existsInFilterList = this.state.activeProviders.includes(provider);
    if (this.state.activeProviders.length > 0) newActiveProviders = this.state.activeProviders;
    if (existsInFilterList) {
      newActiveProviders = newActiveProviders.filter((item) => item !== provider);
    } else if (!existsInFilterList) newActiveProviders.push(provider);
    this.setState({ activeProviders: newActiveProviders });
  }

  generateTypes = () => {
    const { resources } = this.props;
    let allTypes = resources && resources.map((res) => res.resource_type);
    allTypes = new Set(allTypes);
    allTypes = Array.from(allTypes);
    allTypes = alpha(allTypes);
    return allTypes;
  }

  generateTranslatedTypes = () => {
    const { resources, t } = this.props;
    const translatedTypeMap = {};
    if (resources) {
      resources.forEach((res) => {
        translatedTypeMap[res.resource_type] = t(`resourceTypes:sentence.${res.resource_type}`);
      });
    }
    return translatedTypeMap;
  }

  generateYears = () => {
    const { resources } = this.props;
    let allYears = resources && resources.map((res) => res && res.sortByDate.format('YYYY'));
    allYears = new Set(allYears);
    allYears = Array.from(allYears);
    return allYears;
  }

  generateProviders = () => {
    const { resources } = this.props;
    let allProviders = resources && resources.map((res) => {
      if (res && res.institution_name && res.institution_name !== null) return res.institution_name;
      else if (res && res.source === 'patient') return 'my_upload';
      return null;
    });
    allProviders = allProviders.filter((provider) => !!provider);
    allProviders = new Set(allProviders);
    allProviders = Array.from(allProviders);
    allProviders = alpha(allProviders);
    return allProviders;
  }

  toggleCardCheckbox = (resource) => {
    const { selectedCards } = this.state;
    const indexInSelectedCards = selectedCards.indexOf(resource);
    if (indexInSelectedCards > -1) {
      selectedCards.splice(indexInSelectedCards, 1);
    } else {
      // Push the whole resource, even if it is an aggregated object
      selectedCards.push(resource);
    }
    this.setState({ selectedCards });
  }

  shareRecords() {
    const routesTFunction = getRouteTFunction();
    this.props.history.push({
      pathname: routesTFunction('redirectPaths./share-records'),
      state: {
        selectedResources: this.state.selectedCards
      }
    });
  }

  clearFilters = () => {
    this.setState({ activeTypes: [], activeYears: [], activeProviders: [] });
  }

  setSortType = (sortingType) => {
    this.setState({ sortingType });
  }

  filters = (t) => (
    <div>
      <Text paragraph bold>{t('dashboard:sort_by')}</Text>
      <div className='records-filter__list'>
        {Object.keys(sortingData).map((key) => (
          <FilterButton
            key={key}
            item={t(`sortingData:${key}`) || sortingData[key].name}
            name={sortingData[key].label}
            selectedItems={[this.state.sortingType]}
            onSelectFilter={() => this.setState({ sortingType: sortingData[key].label })}
          />))}
      </div>
      <Text paragraph bold>{this.props.t('dashboard:filter_by_year')}</Text>
      <FilterList
        onSelectFilter={this.updateYear}
        listItems={this.generateYears()}
        selectedTypes={this.state.activeYears}
        className='horizontal'
      />
      <Text paragraph bold>{t('dashboard:filter_by_record_type')}</Text>
      <FilterList
        onSelectFilter={this.updateType}
        listItems={this.generateTypes()}
        translatedListItems={this.generateTranslatedTypes()}
        selectedTypes={this.state.activeTypes}
        className='horizontal'
      />
      <Text paragraph bold>{t('dashboard:filter_by_health_provider')}</Text>
      <FilterList
        onSelectFilter={this.updateProvider}
        listItems={this.generateProviders()}
        translatedListItems={{ my_upload: t('immunization:my_upload') }}
        selectedTypes={this.state.activeProviders}
        className='horizontal'
      />
    </div>
  )

  props: Props;

  render() {
    const { user, resources, history, location, loading, t } = this.props;
    const { sortingType, selectedCards, shareSelectOn, selectAll } = this.state;

    const getFilteredResources = resources && this.getFilteredResources(resources);
    const filteredResources: ResourceType[] = getFilteredResources
      .sort((a, b) => {
        // user specified sort order
        if (sortingType === 'dateAsc') return this.sortResources(a, b, 'sortByDate');
        if (sortingType === 'dateDesc') return this.sortResources(a, b, 'sortByDate', false);
        if (sortingType === 'alphaAsc') return this.sortResources(a, b, 'title');
        if (sortingType === 'alphaDesc') return this.sortResources(a, b, 'title', false);
        return 0;
      }).sort((a, b) => {
      // pinned resources
        return this.sortResources(a, b, 'sortingPriority', false);
      });

    const allResources: number = resources && resources.length;
    const totalFilteredResources: number = filteredResources && filteredResources.length;

    const renderFullDash = Boolean(!loading && resources && resources.length > 0 &&
      user && user.data && user.data.email_verified);

    let countText = '';
    switch (allResources) {
    case 0:
      countText = t('dashboard:no_records');
      break;
    case 1:
    case totalFilteredResources:
      countText = t('dashboard:n_records', { count: allResources });
      break;
    default:
      countText = t('dashboard:total_of_all_records', { total: totalFilteredResources, all: allResources });
    }
    return (
      <AppView viewType='private-body'>
        <div className='dashboard'>
          {location.messageText &&
            <Message className={location.messageType}>
              {location.messageText}
            </Message>}
          {loading && <Loader isLoading />}
          {!renderFullDash &&
            <OnboardingStateHeader
              user={user}
              emptyData={!renderFullDash}
              history={history}
              loading={!resources}
            />}
          {renderFullDash && (
            <Section
              title={t('dashboard:my_health_records')}
              subtitle={countText}
              headerContentRight={(
                <span>
                  {shareSelectOn &&
                    <Button
                      className='btn-record-share-secondary'
                      onClick={() => {
                        if (selectAll) {
                          this.setState({ selectedCards: [], selectAll: false });
                        } else {
                          // deep copy
                          this.setState({ selectedCards: filteredResources.map((r) => r), selectAll: true });
                        }
                      }}
                    >
                      {t('dashboard:select_deselect_all')}
                    </Button>
                  }
                  <Button
                    role='button'
                    className={'btn-record-share'}
                    onClick={() => {
                      if (!shareSelectOn) {
                        this.setState({ shareSelectOn: true });
                      } else if (selectedCards.length === 0) {
                        this.setState({ shareSelectOn: false });
                      } else {
                        this.shareRecords();
                      }
                    }}
                  >
                    {shareSelectOn && selectedCards.length > 0 &&
                      t('dashboard:share_n_records', { count: selectedCards.length })
                    }
                    {shareSelectOn && selectedCards.length === 0 && t('dashboard:cancel')}
                    {!shareSelectOn && t('dashboard:share_records')}
                  </Button>
                </span>
              )}
            >
              {/* show on desktop */}
              <div className='records-filter__sidebar'>
                {this.filters(t)}
              </div>
              {/* show on mobile */}
              <Dropdown label={t('dashboard:filters_and_sort')} className='records-filter__dropdown'>
                <div className='records-filter__dropdown__content'>
                  {this.filters(t)}
                </div>
              </Dropdown>
              <DashboardCards
                resources={filteredResources}
                showCheckbox={shareSelectOn}
                toggleCardCheckbox={this.toggleCardCheckbox}
                selectedCards={selectedCards}
              />
            </Section>
          )}
        </div>
      </AppView>
    );
  }
}

const mapStateToProps = (appState) => ({
  user: appState.user,
  resources: appState.resources && appState.resources.data,
  loading: appState.resources && appState.resources.loading
});

const mapDispatchToProps = (dispatch) => ({
  fetchResourcesExcluding: ((params) => dispatch(fetchAllResourcesExclParams(params))),
  fetchUser: (params) => dispatch(userActions.fetchUser(params))
});

export default withTranslation(['dashboard', 'resourceTypes', 'sortingData', 'immunization'])(
  connect(mapStateToProps, mapDispatchToProps)(Dashboard)
);



// WEBPACK FOOTER //
// ./src/components/pages/dashboard.js